import React, { useCallback } from 'react';
import {
  Button,
  DialogActions,
  DialogOpenChangeData,
  DialogProps,
  DialogTrigger,
  Divider,
  mergeClasses,
} from '@fluentui/react-components';
import { I18nModel } from '@bingads-webui/copilot-i18n';

import { InlineCopilotDialog } from '../InlineCopilotDialog';
import { Loading } from '../Loading';
import { Prompt } from './Prompt';
import { useRecommendationDialogStyles } from './useRecommendationDialogStyles.styles';
import { ErrorMessageBar } from '../ErrorMessageBar';

export interface RenderResultProps<TResult> {
  results: TResult[];
  isApplying: boolean;
}

export interface RecommendationDialogProps<TResult> {
  applyingText?: string;
  cancelButtonText?: string;
  className?: string,
  errorMessage?: string;
  i18n: I18nModel;
  isApplying: boolean;
  isLoading: boolean;
  loadingText?: string;
  modalType?: DialogProps['modalType'];
  open: boolean;
  prompt: string;
  promptPlaceholder: string;
  results: TResult[];
  saveButtonText?: string;
  onApply: () => void;
  onClose: () => void;
  onPromptChange: (prompt: string) => void;
  onRenderDescription: () => React.ReactNode;
  onRenderTitle: () => React.ReactNode;
  onRenderResults: (props: RenderResultProps<TResult>) => React.ReactNode;
  onSubmit: (prompt: string) => void;
  isNewLogo?: boolean,
}

export const RecommendationDialog = <TResult extends unknown>(props: RecommendationDialogProps<TResult>): React.ReactElement => {
  const {
    applyingText,
    cancelButtonText,
    className,
    errorMessage,
    i18n,
    isApplying,
    isLoading,
    loadingText,
    modalType,
    open,
    prompt,
    promptPlaceholder,
    results,
    saveButtonText,
    onApply,
    onClose,
    onPromptChange,
    onRenderTitle,
    onRenderDescription,
    onRenderResults,
    onSubmit,
    isNewLogo,
  } = props;

  const classes = useRecommendationDialogStyles();

  const hasResults = results && results.length > 0;

  const handleOpenChange = useCallback((_ev: any, data: DialogOpenChangeData) => {
    if (!data.open) {
      onClose();
    }
  }, [onClose]);

  const handleSubmitPrompt = useCallback(() => {
    const trimmedPrompt = prompt.trim();
    if (trimmedPrompt.length > 0) {
      onSubmit(trimmedPrompt);
    }
  }, [onSubmit, prompt]);

  return (
    <InlineCopilotDialog
      className={className}
      modalType={modalType}
      open={open}
      onOpenChange={handleOpenChange}
      title={<div className="recommendation-dialog-title">{onRenderTitle()}</div>}
      isNewLogo={isNewLogo}
    >
      <div className={mergeClasses('recommendation-dialog-content', classes.root)}>
        <Divider />
        <div className="recommendation-dialog-description">
          {onRenderDescription()}
        </div>
        <div className={mergeClasses('recommendation-dialog-prompt', classes.prompt)}>
          <Prompt
            value={prompt}
            disabled={isLoading || isApplying}
            placeholder={promptPlaceholder}
            onChange={onPromptChange}
            onSubmit={handleSubmitPrompt}
          />
        </div>
        {hasResults && (
          <div className="recommendation-dialog-results">
            {onRenderResults({ results, isApplying })}
          </div>
        )}
        {isApplying && (
          <div className={mergeClasses('recommendation-dialog-applying-message', classes.loading)}>
            <Loading loadingText={applyingText ?? i18n.getString(_TL_('Applying...'))} />
          </div>
        )}
        {isLoading && (
          <div className={mergeClasses('recommendation-dialog-loading-message', classes.loading)}>
            <Loading loadingText={loadingText ?? i18n.getString(_TL_('Loading...'))} />
          </div>
        )}
        {errorMessage && (
          <div className="recommendation-dialog-error-message">
            <ErrorMessageBar>
              {errorMessage}
            </ErrorMessageBar>
          </div>
        )}
        {hasResults && (
          <DialogActions position="start" fluid>
            <Button
              className="recommendation-apply-button"
              appearance="primary"
              disabled={isApplying}
              onClick={onApply}
            >
              {saveButtonText ?? i18n.getString(_TL_('Apply'))}
            </Button>
            <DialogTrigger disableButtonEnhancement>
              <Button
                className="recommendation-cancel-button"
                appearance="secondary"
              >
                {cancelButtonText ?? i18n.getString(_TL_('Cancel'))}
              </Button>
            </DialogTrigger>
          </DialogActions>
        )}
      </div>
    </InlineCopilotDialog>
  );
};

RecommendationDialog.defaultProps = {
  modalType: 'alert',
};
